import { useState, useMemo } from "react";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Inbox, FileText, Bell, Archive, Stamp,
  LogOut, Menu, LucideIcon, FileSearch, UserRoundCheck,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { usePortalNavEstado, type PortalNavItem } from "@/hooks/usePortalNav";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { TabBarPlano } from "@/components/ui/tab-bar";
import { KpiCardUniforme } from "@/components/ui/kpi-card";
import { useNotificacionesLegalFlow } from "@/hooks/useNotificacionesLegalFlow";
import { useNotificacionesLeidas } from "@/hooks/useNotificacionesLeidas";

const BASE = "/admin/legal-flow";

/** Menú "Legal Flow" en BD (`menus.id`). Etiquetas, orden y alta/baja viven en `submenus`. */
const LEGAL_FLOW_MENU_ID = 29;

/** El ícono no vive en BD: se resuelve por ruta. Rutas nuevas sin ícono caen en `FileText`. */
const iconMap: Record<string, LucideIcon> = {
  [BASE]:                              LayoutDashboard,
  [`${BASE}/solicitudes`]:                Inbox,
  [`${BASE}/titularidad`]:               UserRoundCheck,
  [`${BASE}/escrituracion/expedientes`]: Stamp,
  [`${BASE}/validacion-contratos`]:    FileSearch,
  [`${BASE}/plantillas`]:               FileText,
  [`${BASE}/archivados`]:                Archive,
  [`${BASE}/notificaciones`]:           Bell,
};

/**
 * Submenús que en BD son ACCIONES, no secciones: existen para dar permiso (p. ej. crear una
 * solicitud, submenú 174) pero no van en la navegación. Se abren desde el botón de su pantalla.
 */
const RUTAS_ACCION = new Set<string>([`${BASE}/solicitudes/nueva`]);

/**
 * Grupos visuales del sidebar (solo presentación; BD no los modela). Dentro de cada grupo los
 * items llegan en el `orden` de BD. Una ruta nueva que no esté aquí cae en "Otros" para no perderse.
 */
const GRUPOS = ["Operaciones", "Escrituración", "Registro Legal", "Sistema"] as const;
const GRUPO_OTROS = "Otros";
const grupoPorRuta: Record<string, string> = {
  [BASE]:                                "Operaciones",
  [`${BASE}/solicitudes`]:                  "Operaciones",
  [`${BASE}/titularidad`]:                 "Operaciones",
  [`${BASE}/escrituracion/expedientes`]: "Escrituración",
  [`${BASE}/validacion-contratos`]:      "Escrituración",
  [`${BASE}/plantillas`]:                 "Registro Legal",
  [`${BASE}/archivados`]:                  "Registro Legal",
  [`${BASE}/notificaciones`]:             "Sistema",
};

const ROUTE_LABELS: Record<string, string> = {
  "/admin/legal-flow":                              "Inicio",
  "/admin/legal-flow/solicitudes":                     "Solicitudes legales",
  "/admin/legal-flow/solicitudes/nueva":                 "Nueva solicitud",
  "/admin/legal-flow/plantillas":                    "Catálogo de plantillas",
  "/admin/legal-flow/escrituracion/expedientes":    "Expedientes",
  "/admin/legal-flow/archivados":                     "Expedientes archivados",
  "/admin/legal-flow/notificaciones":                "Notificaciones",
  "/admin/legal-flow/validacion-contratos":         "Validación de contratos",
  "/admin/legal-flow/titularidad":                  "Titularidad",
};

/**
 * Avatar con el perfil de la sesión (nombre, rol, correo y "Cerrar sesión"). Mismo popover que el
 * Portal de Cobranza, sin la parte de "Ver como": Legal Flow no impersona. Se usa en el header de
 * escritorio y en el móvil.
 */
function AvatarPerfil({
  userName,
  fotoUrl,
  userRole,
  email,
  onSignOut,
}: {
  fotoUrl?: string | null;
  userName: string;
  userRole: string;
  email?: string | null;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative flex shrink-0 rounded-full transition-all duration-200 focus:outline-none hover:ring-2 hover:ring-primary/35 hover:ring-offset-1 hover:ring-offset-card"
          aria-label="Perfil"
        >
          <AvatarPersona nombre={userName} fotoUrl={fotoUrl} size="sm" />
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-[1.5px] border-card bg-emerald-400" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-64 p-0 overflow-hidden rounded-xl shadow-xl border border-border/50"
      >
        <div className="relative px-4 pt-4 pb-3.5 border-b border-border/60">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary rounded-t-xl" />
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <AvatarPersona nombre={userName} fotoUrl={fotoUrl} size="md" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background bg-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[13.5px] font-semibold text-foreground truncate leading-tight">{userName}</p>
              <p className="text-[11px] text-muted-foreground truncate mt-0.5 leading-tight">{userRole}</p>
            </div>
          </div>
          {email && (
            <p className="mt-2.5 text-[10.5px] text-muted-foreground/70 truncate font-mono tracking-tight">{email}</p>
          )}
        </div>

        <div className="p-1.5">
          <button
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-destructive hover:bg-destructive/8 transition-colors duration-150"
          >
            <LogOut className="size-3.5 shrink-0" />
            Cerrar sesión
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LegalFlowLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { items: navFromDb, cargando: navCargando } =
    usePortalNavEstado(LEGAL_FLOW_MENU_ID, iconMap, FileText);
  const { isPathAllowed, isLoading: permisosCargando } = useAllowedMenus();
  const cargandoNav = navCargando || permisosCargando;

  // Nav desde BD filtrada por el permiso de lectura del rol (`isPathAllowed` ya descarta vistas
  // apagadas). Antes era una lista fija del front filtrada solo por `isPathDisabled`, así que un
  // rol sin permiso veía todas las entradas. Se agrupa por `grupoPorRuta` conservando el orden de BD.
  const visibleNavGroups = useMemo(() => {
    const porGrupo = new Map<string, PortalNavItem[]>();
    for (const item of navFromDb) {
      if (RUTAS_ACCION.has(item.path) || !isPathAllowed(item.path)) continue;
      const grupo = grupoPorRuta[item.path] ?? GRUPO_OTROS;
      porGrupo.set(grupo, [...(porGrupo.get(grupo) ?? []), item]);
    }
    return [...GRUPOS, GRUPO_OTROS]
      .filter((label) => porGrupo.has(label))
      .map((label) => ({ label, items: porGrupo.get(label)! }));
  }, [navFromDb, isPathAllowed]);

  // La campana cuenta las notificaciones REALES (mismo hook que la pantalla de
  // Notificaciones) y descuenta las ya leidas, que viven en un store
  // persistido. Antes contaba el total, asi que marcarlas como leidas no bajaba
  // el numero del header.
  const { data: notificaciones = [] } = useNotificacionesLegalFlow();
  const { sinLeer } = useNotificacionesLeidas();
  const unreadCount = sinLeer(notificaciones);
  const { canReturnToAdmin } = useCanReturnToAdmin();
  // Detalle de expediente: el header dice de cual, no solo "Expediente".
  const folioExpediente = location.pathname.match(/\/cases\/([^/]+)$/)?.[1];
  // Detalle de una solicitud de Titularidad: el header sigue diciendo de qué sección es.
  const enTitularidad = location.pathname.startsWith(`${BASE}/titularidad/`);
  const currentLabel =
    ROUTE_LABELS[location.pathname] ||
    (enTitularidad ? "Titularidad" : null) ||
    (folioExpediente ? `Expediente ${decodeURIComponent(folioExpediente)}` : "Expediente");

  const rawName = profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const userRole = profile?.rol_nombre ?? "Legal";
  const initials = userName.split(" ").filter(Boolean).slice(0, 2).map((p: string) => p.charAt(0).toUpperCase()).join("") || "U";
  const fotoVista = useFotoPerfilVista();
  // Un elemento por header: solo uno se ve a la vez (lg: escritorio, < lg: móvil).
  const perfil = (
    <AvatarPerfil
      fotoUrl={fotoVista}
      userName={userName}
      userRole={userRole}
      email={profile?.email}
      onSignOut={() => signOut()}
    />
  );

  // El panel (raíz del portal) solo se marca en su ruta exacta; el resto incluye subrutas.
  const isActive = (path: string) => {
    if (path === BASE) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const go = (path: string) => { navigate(path); setMobileOpen(false); };

  const sidebar = (
    <PortalSidebar
      portal="Portal Legal Flow"
      cargando={cargandoNav}
      groups={visibleNavGroups.map((group) => ({
        label: group.label,
        items: group.items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => go(item.path),
        })),
      }))}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => go("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <PortalTrackingProvider portal="legal-flow">
      <div className="min-h-screen flex antialiased">
        {/* Desktop sidebar */}
        <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
          {sidebar}
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
            {sidebar}
          </SheetContent>
        </Sheet>

        <div className="flex-1 lg:pl-64 min-w-0">
          {/* Desktop header. Misma anatomia que el del portal de cobranza:
              titulo de seccion a la izquierda, acciones a la derecha, barra
              translucida pegada al scroll. */}
          <header className="hidden lg:flex items-center justify-between gap-3 sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border-soft px-6 h-14 shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]">
            <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-foreground tracking-tight">{currentLabel}</h1>

            <div className="flex shrink-0 items-center gap-2.5">
              <Link
                to="/admin/legal-flow/notificaciones"
                aria-label={
                  unreadCount > 0
                    ? `Notificaciones: ${unreadCount} pendientes`
                    : "Notificaciones"
                }
                className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <Bell className="h-4 w-4" strokeWidth={1.75} />
                {unreadCount > 0 && (
                  <span className="absolute right-0.5 top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold tabular-nums text-destructive-foreground">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Link>

              {perfil}
            </div>
          </header>

          {/* Mobile header */}
          <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-between gap-2 px-3 h-14">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setMobileOpen(true)}
                  className="p-1.5 -ml-1 rounded-lg text-foreground hover:bg-muted transition-colors"
                  aria-label="Abrir menú"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <p className="text-[16px] font-bold text-foreground tracking-tight truncate">{currentLabel}</p>
              </div>
              {perfil}
            </div>
          </header>

          {/* Mismo marco que el Portal de Cobranza: el padding lo pone el layout (las páginas
              no traen el suyo), pestañas solo con texto y tarjetas KPI de un solo tamaño. */}
          <main className="min-w-0 overflow-x-clip px-4 py-5 sm:px-8 bg-background min-h-[calc(100vh-56px)]">
            <TabBarPlano>
              <KpiCardUniforme>
                <Outlet />
              </KpiCardUniforme>
            </TabBarPlano>
          </main>
        </div>
      </div>
    </PortalTrackingProvider>
  );
}
